/**
 * Closing Disclosure Types
 *
 * Type definitions for the customer-friendly Closing Disclosure screen.
 * All display text (labels, descriptions) use i18n keys, NOT string literals.
 * Financial amounts are stored in cents to avoid floating-point precision issues.
 */

/**
 * PartyRole - Enum for transaction roles
 * Language-agnostic IDs used to identify party types
 */
// eslint-disable-next-line no-shadow
export enum PartyRole {
  BUYER = 'buyer',
  SELLER = 'seller',
  LENDER = 'lender',
  TITLE_COMPANY = 'title_company',
  AGENT = 'agent',
}

/**
 * ClosingDisclosureCharge - A single charge/credit line
 *
 * Represents one line item on the Closing Disclosure with full metadata.
 * Amount is stored in cents to maintain precision (e.g., $129,027.50 = 12,902,750 cents).
 */
export interface ClosingDisclosureCharge {
  readonly id: string; // Unique identifier for this charge
  readonly letter: string; // Closing Disclosure letter (A, B, C, D, G, etc.)
  readonly number: number; // Sequential number within letter (1, 2, 3, etc.)
  readonly description_key: string; // i18n key for charge description (e.g., 'charges.title-insurance')
  readonly amount_cents: number; // Amount in cents (never use decimals)
  readonly payer_role: PartyRole | null; // Which role pays this charge (null if not applicable)
  readonly payee_id?: string; // Party UUID of payee if external entity
  readonly payee_name?: string; // Display name of payee
  readonly details_key?: string; // i18n key for expanded detail section
}

/**
 * RoleSummary - Summary totals for one party role
 *
 * Aggregates all charges/credits for a specific role (Buyer, Seller, etc.)
 * and provides a list of related detail line items.
 */
export interface RoleSummary {
  readonly role: PartyRole; // Which role this summary represents
  readonly role_label_key: string; // i18n key for role display name (e.g., 'closing-disclosure.role-buyer')
  readonly total_due_cents: number; // Total amount owed (buyer) or proceeds (seller) in cents
  readonly detail_line_items: ClosingDisclosureCharge[]; // All charges attributable to this role
  readonly is_active: boolean; // Whether this role has activity in the transaction
}

/**
 * ExpandableSection - Grouping of related charges
 *
 * Organizes charges into collapsible/expandable sections for better UX.
 * Examples: "Title Insurance", "Loan Costs", "Taxes & Government Fees"
 */
export interface ExpandableSection {
  readonly id: string; // Unique section identifier
  readonly title_key: string; // i18n key for section title (e.g., 'closing-disclosure.section-title-insurance')
  readonly charges: ClosingDisclosureCharge[]; // All charges in this section
  readonly is_expanded?: boolean; // Default expand/collapse state (true = expanded)
}

/**
 * EscrowBreakdown - Detailed escrow funds breakdown
 *
 * Shows what escrow funds are reserved for (insurance, taxes, etc.)
 */
export interface EscrowBreakdown {
  readonly insurance_cents: number; // Escrow for homeowners insurance (in cents)
  readonly taxes_cents: number; // Escrow for property taxes (in cents)
  readonly hoa_fees_cents?: number; // Escrow for HOA fees if applicable (in cents)
  readonly other_cents?: number; // Any other escrow items (in cents)
}

/**
 * ClosingDisclosureData - Main data structure for display
 *
 * Complete Closing Disclosure information ready for customer-facing UI.
 * Contains transaction overview, role summaries, charges, settlement balancing, and escrow details.
 */
export interface ClosingDisclosureData {
  readonly order_id: string; // The order identifier
  readonly property_address: string; // Full property address for reference
  readonly closing_date: string; // ISO 8601 date string (e.g., '2026-07-15')
  readonly purchase_price_cents: number; // Purchase price in cents
  readonly loan_amount_cents: number; // Loan amount in cents
  readonly role_summaries: RoleSummary[]; // Summaries for each active role
  readonly charge_sections: ExpandableSection[]; // Charges organized by section
  readonly escrow_total_cents: number; // Total escrow amount in cents
  readonly escrow_detail?: EscrowBreakdown; // Detailed escrow breakdown
  readonly settlement_balancing?: {
    readonly buyer_cash_at_closing_cents: number; // Cash buyer brings to closing
    readonly seller_proceeds_cents: number; // Net proceeds seller receives
    readonly lender_funding_cents: number; // Loan amount lender provides
  };
}

/**
 * ClosingDisclosureState - Component state for Closing Disclosure screen
 *
 * Manages loading, expansion state, and error handling for the disclosure.
 */
export interface ClosingDisclosureState {
  readonly data: ClosingDisclosureData | null;
  readonly is_loading: boolean;
  readonly error: string | null;
  readonly expanded_sections: Set<string>; // Track which sections are expanded
}
